import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alpha3Code } from 'i18n-iso-countries'

import { CardContent } from '@percent/lemonade'
import { ReactComponent as PencilSVG } from '@percent/admin-dashboard/common/assets/images/pencil.svg'
import { PaymentMethod } from '@percent/admin-dashboard/api/types'
import styles from './PaymentDetails.module.scss'
import { ListItemButton } from '@percent/admin-dashboard/common/components/listItemButton/ListItemButton'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { UpdatePaymentMethodModal } from './UpdatePaymentMethodModal'
import { usePermissions } from '@percent/admin-dashboard/common/hooks'

export function PaymentTypeWithButton({
  title,
  text,
  paymentMethod,
  refresh,
  isUpdateAvailable = true,
  organisationCountryCode
}: Readonly<{
  title: string
  text: string
  refresh: VoidFunction
  paymentMethod?: PaymentMethod
  isUpdateAvailable?: boolean
  organisationCountryCode: Alpha3Code
}>) {
  const [openUpdatePaymentMethodModal, setOpenUpdatePaymentMethodModal] = useState(false)
  const { t } = useTranslation()
  const paymentMethodsSubmitFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_PAYMENT_METHODS_SUBMIT)
  const internalFgaDoraBankDetailsPaymentMethodsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_BANK_DETAILS_PAYMENT_METHODS
  )
  const { userHasPermissions } = usePermissions()

  const canUpdatePaymentMethod =
    !internalFgaDoraBankDetailsPaymentMethodsFeatureFlag || userHasPermissions('bank_details_payment_methods:can_set')

  const handleUpdatePaymentMethodModal = () => {
    setOpenUpdatePaymentMethodModal(prevState => !prevState)
  }

  const isValidMethod = paymentMethod?.method === 'card' || paymentMethod?.method === 'cheque'

  return (
    <>
      <CardContent title={title} testId="payment-method">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            height: '24px'
          }}
        >
          {text}
          {paymentMethodsSubmitFeatureFlag &&
            canUpdatePaymentMethod &&
            isUpdateAvailable &&
            organisationCountryCode !== 'IND' && (
              <ListItemButton
                className={styles.editButton}
                title={
                  isValidMethod
                    ? t('button.paymentDetails.updatePaymentMethod')
                    : t('button.paymentDetails.setPaymentMethod')
                }
                onClick={handleUpdatePaymentMethodModal}
                testId="payment-method-action"
                icon={<PencilSVG />}
              />
            )}
        </div>
      </CardContent>
      <UpdatePaymentMethodModal
        open={openUpdatePaymentMethodModal}
        onClose={handleUpdatePaymentMethodModal}
        refresh={refresh}
        paymentMethod={isValidMethod ? (paymentMethod as PaymentMethod & { method: 'card' | 'cheque' }) : undefined}
        organisationCountryCode={organisationCountryCode}
      />
    </>
  )
}
